import {
  blockKey,
  captureBlocks,
  stillMissing,
  trackingPayload,
  type CaptureBlock,
  type TrackedItem,
  type TrackingPayload,
} from '@/features/sales/tracking-capture';

import type { CartLine } from './cart';

/**
 * A SERIAL AT THE COUNTER IS ANOTHER SCAN — SAL-06's half of BR-MD-03-17.
 *
 * The rules are the document screens' rules and are not restated here: the
 * blocks a line must fill come from the SAME `captureBlocks` the invoice and
 * delivery grids use, because "each component behaves on a document exactly
 * as it would if somebody had typed it on a line by itself" (R7) does not
 * stop applying because the sale was rung up at a shop counter.
 *
 * What is different is the INTERACTION, and it follows §5c's one sentence
 * about how a till takes input: *"Pressing a key runs a handler, so a barcode
 * scanner that ends with Enter adds the item straight to the cart."* A till
 * has exactly one text box and a cashier who is holding the goods. So the
 * capture is not a form and not a sub-table — it is that same box, retargeted:
 * while anything in the cart is still unidentified, the box asks for the next
 * identifier and Enter files it. Scan the item, scan its serial, scan the next
 * item. Nothing is typed that a scanner cannot send.
 *
 * The queue is ordered and singular on purpose. "Which of the four things in
 * this cart is this serial for?" is a question a counter cannot afford; the
 * screen names one, and the cashier answers that one.
 */

/** A till line's tracking facts in the shape the shared rules understand. */
export function asTrackedItem(line: CartLine): TrackedItem {
  return {
    id: line.itemId,
    name: line.name,
    type: line.autoDeduct ? 'bundle' : 'product',
    autoDeduct: line.autoDeduct,
    trackingType: line.tracking,
    components: line.components.map((component) => ({
      componentItemId: component.componentItemId,
      componentName: component.componentName,
      componentTracking: component.tracking,
      baseQty: component.baseQty,
    })),
  };
}

export function blocksFor(line: CartLine): CaptureBlock[] {
  return captureBlocks(asTrackedItem(line), String(line.qty));
}

export interface PendingScan {
  itemId: string;
  block: CaptureBlock;
  key: string;
  /** How many identifiers this block is still short of. */
  missing: number;
  /** Which one is being asked for, 1-based — "serial 2 of 3". */
  position: number;
}

/**
 * The ONE thing the scan box is asking for right now: the first block, in
 * cart order, that is still short. Null means the cart is fully identified
 * and the box goes back to finding products.
 */
export function nextScan(cart: readonly CartLine[]): PendingScan | null {
  for (const line of cart) {
    for (const block of blocksFor(line)) {
      const key = blockKey(block);
      const missing = stillMissing(block, line.identifiers[key] ?? '');
      if (missing === 0) continue;
      return {
        itemId: line.itemId,
        block,
        key,
        missing,
        position: (block.needed ?? 1) - missing + 1,
      };
    }
  }
  return null;
}

/** Every identifier already filed against one block, in scan order. */
export function scannedFor(line: CartLine, key: string): string[] {
  const typed = line.identifiers[key] ?? '';
  return typed
    .split(',')
    .map((piece) => piece.trim())
    .filter((piece) => piece !== '');
}

/**
 * Files one scan. A serial JOINS what is already there — a scanner sends one
 * code per press, and three units means three presses — while a lot number
 * REPLACES it, because a batch block holds exactly one.
 */
export function fileScan(
  cart: readonly CartLine[],
  pending: PendingScan,
  scanned: string,
): CartLine[] {
  const value = scanned.trim();
  if (value === '') return [...cart];
  return cart.map((line) => {
    if (line.itemId !== pending.itemId) return line;
    const existing = scannedFor(line, pending.key);
    const next =
      pending.block.kind === 'batch'
        ? value
        : [...existing.filter((piece) => piece !== value), value].join(', ');
    return { ...line, identifiers: { ...line.identifiers, [pending.key]: next } };
  });
}

/** Takes one identifier back off a line — a mis-scan at a counter is normal. */
export function unfileScan(
  cart: readonly CartLine[],
  itemId: string,
  key: string,
  value: string,
): CartLine[] {
  return cart.map((line) => {
    if (line.itemId !== itemId) return line;
    const kept = scannedFor(line, key).filter((piece) => piece !== value);
    return { ...line, identifiers: { ...line.identifiers, [key]: kept.join(', ') } };
  });
}

/** One cart line as `completeSale` wants it, identifiers included. */
export function lineTracking(line: CartLine): TrackingPayload {
  return trackingPayload(blocksFor(line), line.identifiers);
}

/**
 * A refund line's "which one is coming back", from the chips the supervisor
 * tapped. BR-MD-03-17 runs both ways, so this is the same payload shape the
 * sale sends — the server checks it with the same validator.
 *
 * A BATCH block needs no choosing: one lot went out on that line, and the
 * same lot is what comes back. Asking a supervisor to retype it would be a
 * question with one possible answer.
 */
export function returningTracking(
  sold: readonly {
    componentItemId: string | null;
    tracking: 'serial' | 'batch';
    serialNos: string[];
    batchNo: string | null;
  }[],
  saleLineId: string,
  picked: Readonly<Record<string, string[]>>,
): TrackingPayload {
  const payload: TrackingPayload = {};
  const components: NonNullable<TrackingPayload['components']> = [];

  for (const block of sold) {
    let chosen: { serialNos: string[] } | { batchNo: string };
    if (block.tracking === 'batch') {
      if (block.batchNo === null) continue;
      chosen = { batchNo: block.batchNo };
    } else {
      const serials = picked[`${saleLineId}|${block.componentItemId ?? ''}`] ?? [];
      if (serials.length === 0) continue;
      chosen = { serialNos: serials };
    }
    if (block.componentItemId === null) Object.assign(payload, chosen);
    else components.push({ componentItemId: block.componentItemId, ...chosen });
  }

  if (components.length > 0) payload.components = components;
  return payload;
}
